import React, { useEffect, useMemo, useState } from 'react';
import EcxDatePicker, { EcxDateRangePicker } from '../../../common/EcxDatePicker';
import {
    ACTIVITY_MEDIUM_TYPES,
    ACTIVITY_SEARCH_COLUMNS,
    ACTIVITY_STATUSES,
    DEFAULT_ACTIVITY_FILTERS,
    applyActivitySearch,
    getActiveFilterChips,
    getActivitySearchColumn,
    getActivitySearchQuery,
} from './activityQuery';

function ActivityFilters({
    filters,
    appliedFilters,
    onFiltersChange,
    onApply,
    onQuickFilter,
    onClear,
    onRemoveChip,
    loading = false,
}) {
    const [expanded, setExpanded] = useState(false);
    const [searchColumn, setSearchColumn] = useState(() => getActivitySearchColumn(filters));
    const [searchQuery, setSearchQuery] = useState(() => getActivitySearchQuery(filters, searchColumn));
    const [dateMode, setDateMode] = useState(
        appliedFilters.dateFrom || appliedFilters.dateTo ? 'range' : 'single'
    );

    useEffect(() => {
        const column = getActivitySearchColumn(filters);
        setSearchColumn(column);
        setSearchQuery(getActivitySearchQuery(filters, column));
    }, [filters]);

    useEffect(() => {
        if (appliedFilters.dateFrom || appliedFilters.dateTo) {
            setDateMode('range');
        } else if (appliedFilters.date) {
            setDateMode('single');
        }
    }, [appliedFilters.date, appliedFilters.dateFrom, appliedFilters.dateTo]);

    const activeChips = useMemo(
        () => getActiveFilterChips(appliedFilters),
        [appliedFilters]
    );

    const pendingChanges = useMemo(
        () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
        [filters, appliedFilters]
    );

    const searchMeta = useMemo(
        () =>
            ACTIVITY_SEARCH_COLUMNS.find((column) => column.value === searchColumn) ||
            ACTIVITY_SEARCH_COLUMNS[0],
        [searchColumn]
    );

    const handleQuickFilter = (field, value) => {
        onQuickFilter(field, value);
    };

    const updateFilter = (field, value) => {
        onFiltersChange((prev) => {
            const next = { ...prev, [field]: value };

            if (field === 'date' && value) {
                next.dateFrom = '';
                next.dateTo = '';
            }

            if ((field === 'dateFrom' || field === 'dateTo') && value) {
                next.date = '';
            }

            return next;
        });
    };

    const handleSearchColumnChange = (column) => {
        setSearchColumn(column);
        setSearchQuery('');
        onFiltersChange((prev) => applyActivitySearch(prev, column, ''));
    };

    const handleSearchQueryChange = (value) => {
        setSearchQuery(value);
        onFiltersChange((prev) => applyActivitySearch(prev, searchColumn, value));
    };

    const handleDateModeChange = (mode) => {
        setDateMode(mode);
        onFiltersChange((prev) => ({
            ...prev,
            date: mode === 'single' ? prev.date : '',
            dateFrom: mode === 'range' ? prev.dateFrom : '',
            dateTo: mode === 'range' ? prev.dateTo : '',
        }));
    };

    const handleApply = () => {
        onApply(applyActivitySearch(filters, searchColumn, searchQuery));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleApply();
        }
    };

    const handleClear = () => {
        setSearchColumn('patientName');
        setSearchQuery('');
        setDateMode('single');
        onClear();
    };

    return (
        <div className="activity-filter-panel">
            <div className="activity-filter-panel__toolbar">
                <div className="activity-filter-panel__search">
                    <select
                        className="form-select form-select-sm activity-filter-panel__field"
                        value={searchColumn}
                        onChange={(event) => handleSearchColumnChange(event.target.value)}
                        disabled={loading}
                        aria-label="Search by"
                    >
                        {ACTIVITY_SEARCH_COLUMNS.map((column) => (
                            <option key={column.value} value={column.value}>
                                {column.label}
                            </option>
                        ))}
                    </select>

                    <div className="input-group input-group-sm activity-filter-panel__query">
                        <span className="input-group-text bg-white border-end-0">
                            <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm border-start-0"
                            placeholder={searchMeta.placeholder}
                            value={searchQuery}
                            onChange={(event) => handleSearchQueryChange(event.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-accent"
                            onClick={handleApply}
                            disabled={loading}
                        >
                            Search
                        </button>
                    </div>
                </div>

                <div className="activity-filter-panel__quick">
                    <select
                        className="form-select form-select-sm activity-filter-panel__select"
                        value={filters.mediumType}
                        onChange={(event) => handleQuickFilter('mediumType', event.target.value)}
                        disabled={loading}
                        aria-label="Medium"
                    >
                        {ACTIVITY_MEDIUM_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                                Medium: {option.label}
                            </option>
                        ))}
                    </select>

                    <select
                        className="form-select form-select-sm activity-filter-panel__select"
                        value={filters.status}
                        onChange={(event) => handleQuickFilter('status', event.target.value)}
                        disabled={loading}
                        aria-label="Status"
                    >
                        {ACTIVITY_STATUSES.map((option) => (
                            <option key={option.value} value={option.value}>
                                Status: {option.label}
                            </option>
                        ))}
                    </select>

                    <EcxDatePicker
                        className="activity-filter-panel__date-picker"
                        value={filters.date}
                        onChange={(value) => {
                            setDateMode('single');
                            handleQuickFilter('date', value);
                        }}
                        placeholder="Any date"
                        disabled={loading || dateMode === 'range'}
                        size="sm"
                    />

                    <button
                        type="button"
                        className={`btn btn-sm ${expanded ? 'btn-accent' : 'btn-outline-secondary'} activity-filter-panel__toggle`}
                        onClick={() => setExpanded((prev) => !prev)}
                        disabled={loading}
                    >
                        <i className="bi bi-sliders"></i>
                        <span>More</span>
                        {activeChips.length > 0 && (
                            <span className="activity-filter-panel__count">{activeChips.length}</span>
                        )}
                    </button>

                    {(activeChips.length > 0 || pendingChanges) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClear}
                            disabled={loading}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="activity-filter-panel__expanded">
                    <div className="activity-filter-advanced">
                        <div className="activity-filter-advanced__intro">
                            <div className="activity-filter-advanced__icon" aria-hidden="true">
                                <i className="bi bi-calendar3"></i>
                            </div>
                            <div className="min-width-0">
                                <h3 className="activity-filter-advanced__title">Date filters</h3>
                                <p className="activity-filter-advanced__subtitle mb-0">
                                    Filter by a single day or a custom range
                                </p>
                            </div>
                        </div>

                        <div className="activity-filter-advanced__controls">
                            <div
                                className="activity-filter-date-mode"
                                role="group"
                                aria-label="Date filter mode"
                            >
                                <button
                                    type="button"
                                    className={`activity-filter-date-mode__btn ${
                                        dateMode === 'single' ? 'is-active' : ''
                                    }`}
                                    onClick={() => handleDateModeChange('single')}
                                    disabled={loading}
                                >
                                    Single day
                                </button>
                                <button
                                    type="button"
                                    className={`activity-filter-date-mode__btn ${
                                        dateMode === 'range' ? 'is-active' : ''
                                    }`}
                                    onClick={() => handleDateModeChange('range')}
                                    disabled={loading}
                                >
                                    Range
                                </button>
                            </div>

                            <div className="activity-filter-advanced__picker">
                                {dateMode === 'range' ? (
                                    <EcxDateRangePicker
                                        startValue={filters.dateFrom}
                                        endValue={filters.dateTo}
                                        onStartChange={(value) => updateFilter('dateFrom', value)}
                                        onEndChange={(value) => updateFilter('dateTo', value)}
                                        disabled={loading}
                                        size="sm"
                                    />
                                ) : (
                                    <EcxDatePicker
                                        value={filters.date}
                                        onChange={(value) => updateFilter('date', value)}
                                        placeholder="Select a date"
                                        disabled={loading}
                                        size="sm"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="activity-filter-advanced__actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setSearchColumn('patientName');
                                    setSearchQuery('');
                                    onFiltersChange(DEFAULT_ACTIVITY_FILTERS);
                                    setDateMode('single');
                                }}
                                disabled={loading}
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-accent"
                                onClick={handleApply}
                                disabled={loading}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeChips.length > 0 && (
                <div className="activity-filter-panel__chips">
                    <span className="activity-filter-panel__chips-label">Active</span>
                    <div className="activity-filter-chips">
                        {activeChips.map((chip) => (
                            <button
                                key={`${chip.field}-${chip.label}`}
                                type="button"
                                className="activity-filter-chip"
                                onClick={() => onRemoveChip(chip.field, chip.clearValue)}
                                disabled={loading}
                                title="Remove filter"
                            >
                                {chip.label}
                                <i className="bi bi-x-lg"></i>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ActivityFilters;
